"use client";

import { useState } from "react";
import { Icon } from "@iconify/react";
import { uiIcons } from "@/config/icons";
import { usePathname } from "next/navigation";
import { getLocaleFromPathname } from "@/lib/i18n";
import { getToolUiCopy } from "@/lib/tool-ui-copy";
import { tools } from "@/config/tools";
import { getLocalizedTool } from "@/lib/tool-localization";

export function LoremIpsum() {
  const [paragraphs, setParagraphs] = useState(3);
  const [wordsPerParagraph, setWordsPerParagraph] = useState(50);
  const [outputText, setOutputText] = useState("");
  const pathname = usePathname() || "/";
  const locale = getLocaleFromPathname(pathname);
  const ui = getToolUiCopy(locale);
  const localizedTool = getLocalizedTool(
    tools.find((item) => item.id === "lorem-ipsum") ?? tools[0],
    locale
  );

  const loremWords = [
    "lorem", "ipsum", "dolor", "sit", "amet", "consectetur", "adipiscing", "elit",
    "sed", "do", "eiusmod", "tempor", "incididunt", "ut", "labore", "et", "dolore",
    "magna", "aliqua", "enim", "ad", "minim", "veniam", "quis", "nostrud",
    "exercitation", "ullamco", "laboris", "nisi", "aliquip", "ex", "ea", "commodo",
    "consequat", "duis", "aute", "irure", "in", "reprehenderit", "voluptate",
    "velit", "esse", "cillum", "fugiat", "nulla", "pariatur", "excepteur", "sint",
    "occaecat", "cupidatat", "non", "proident", "sunt", "culpa", "qui", "officia",
    "deserunt", "mollit", "anim", "id", "est", "laborum"
  ];

  const generateLorem = () => {
    const result: string[] = [];

    for (let i = 0; i < paragraphs; i++) {
      const words: string[] = [];
      for (let j = 0; j < wordsPerParagraph; j++) {
        const randomWord = loremWords[Math.floor(Math.random() * loremWords.length)];
        words.push(j === 0 ? randomWord.charAt(0).toUpperCase() + randomWord.slice(1) : randomWord);
      }
      result.push(words.join(" ") + ".");
    }

    setOutputText(result.join("\n\n"));
  };

  const handleCopy = async () => {
    await navigator.clipboard.writeText(outputText);
  };

  return (

    <div className="space-y-8">
      <div className="rounded-[20px] border border-positivus-dark/10 bg-positivus-green/10 p-6">
        <div className="flex items-start gap-4">
          <div className="hidden md:block rounded-full bg-positivus-green/20 p-2">
            <Icon icon="mdi:format-text" className="h-6 w-6 text-positivus-dark" />
          </div>
          <div className="text-sm text-positivus-dark w-full">
            <p className="font-medium text-lg mb-2">{localizedTool.name}</p>
            <p className="leading-relaxed">{localizedTool.description}</p>
          </div>
        </div>
      </div>

      <div className="grid gap-6 sm:grid-cols-2">
        <div className="rounded-[20px] border-2 border-positivus-dark bg-white p-6 shadow-[0_5px_0_0_#191A23]">
          <label className="mb-3 block text-lg font-medium text-positivus-dark">
            {ui.numberOfParagraphs}
          </label>
          <input
            type="number"
            min="1"
            max="20"
            value={paragraphs}
            onChange={(e) => setParagraphs(parseInt(e.target.value) || 1)}
            className="w-full rounded-[15px] border-2 border-positivus-dark/20 bg-gray-50 px-4 py-3 text-positivus-dark focus:border-positivus-dark focus:outline-none focus:ring-0 transition-all font-bold text-lg"
          />
        </div>

        <div className="rounded-[20px] border-2 border-positivus-dark bg-white p-6 shadow-[0_5px_0_0_#191A23]">
          <label className="mb-3 block text-lg font-medium text-positivus-dark">
            {ui.wordsPerParagraph}
          </label>
          <input
            type="number"
            min="10"
            max="200"
            value={wordsPerParagraph}
            onChange={(e) => setWordsPerParagraph(parseInt(e.target.value) || 50)}
            className="w-full rounded-[15px] border-2 border-positivus-dark/20 bg-gray-50 px-4 py-3 text-positivus-dark focus:border-positivus-dark focus:outline-none focus:ring-0 transition-all font-bold text-lg"
          />
        </div>
      </div>

      <button
        onClick={generateLorem}
        className="w-full rounded-[20px] bg-positivus-dark px-6 py-4 text-xl font-medium text-white hover:bg-positivus-green hover:text-positivus-dark transition-all shadow-[0_5px_0_0_#191A23] hover:shadow-none hover:translate-y-[2px]"
      >
        {ui.generate}
      </button>

      {outputText && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <label className="text-lg font-medium text-positivus-dark">{ui.generatedText}</label>
            <button
              onClick={handleCopy}
              className="flex items-center gap-2 rounded-[10px] bg-positivus-green/20 px-3 py-1.5 text-sm font-bold text-positivus-dark hover:bg-positivus-green hover:text-white transition-colors"
              title={ui.copyToClipboard}
            >
              <Icon icon={uiIcons.copy} className="h-4 w-4" />
              {ui.copy}
            </button>
          </div>
          <div className="max-h-[500px] overflow-y-auto rounded-[20px] border-2 border-positivus-dark bg-white p-6 shadow-sm">
            <p className="whitespace-pre-wrap text-base leading-relaxed text-gray-600">{outputText}</p>
          </div>
        </div>
      )}
    </div>
  );
}
